import { useState, useEffect } from 'react'
import { X, DollarSign } from 'lucide-react'
import type { FinancialInstitution, LoanProduct, LoanProductCreate } from '../api/adminApi'
import { POLICY_TAG_CATEGORIES } from '../constants/policyTags'
import { formatNumberWithCommas, stripToNumber } from '../utils/formatCurrency'

const PRODUCT_TYPES = [
  { value: 'variable', label: 'Variable Rate' },
  { value: 'fixed_1yr', label: 'Fixed 1 Year' },
  { value: 'fixed_2yr', label: 'Fixed 2 Years' },
  { value: 'fixed_3yr', label: 'Fixed 3 Years' },
  { value: 'fixed_4yr', label: 'Fixed 4 Years' },
  { value: 'fixed_5yr', label: 'Fixed 5 Years' },
  { value: 'split', label: 'Split (Variable + Fixed)' },
]

interface Props {
  product?: LoanProduct | null
  institutions: FinancialInstitution[]
  onSubmit: (data: LoanProductCreate) => Promise<void>
  onClose: () => void
}

const EMPTY_FORM: LoanProductCreate = {
  institution_id: '',
  product_name: '',
  product_type: 'variable',
  interest_rate: 0,
  comparison_rate: 0,
  max_lvr: 80,
  fees: { application_fee: 0, annual_fee: 0, discharge_fee: 0 },
  features: { offset_account: false, redraw_facility: false, extra_repayments: false, portability: false },
  policy_tags: {},
  loan_purpose: null,
  is_active: true,
  source_note: '',
}

function productToForm(p: LoanProduct): LoanProductCreate {
  return {
    institution_id: p.institution_id,
    product_name: p.product_name,
    product_type: p.product_type,
    interest_rate: p.interest_rate,
    comparison_rate: p.comparison_rate,
    max_lvr: p.max_lvr,
    fees: {
      application_fee: p.fees?.application_fee ?? 0,
      annual_fee: p.fees?.annual_fee ?? 0,
      discharge_fee: p.fees?.discharge_fee ?? 0,
    },
    features: {
      offset_account: p.features?.offset_account ?? false,
      redraw_facility: p.features?.redraw_facility ?? false,
      extra_repayments: p.features?.extra_repayments ?? false,
      portability: p.features?.portability ?? false,
    },
    policy_tags: p.policy_tags ?? {},
    loan_purpose: p.loan_purpose ?? null,
    is_active: p.is_active,
    source_note: p.source_url ?? '',
  }
}

export default function LoanProductForm({ product, institutions, onSubmit, onClose }: Props) {
  const isEdit = !!product
  const [form, setForm] = useState<LoanProductCreate>(product ? productToForm(product) : EMPTY_FORM)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    setForm(product ? productToForm(product) : { ...EMPTY_FORM, institution_id: institutions[0]?.id ?? '' })
    setError('')
  }, [product, institutions])

  const set = <K extends keyof LoanProductCreate>(key: K, value: LoanProductCreate[K]) =>
    setForm((f) => ({ ...f, [key]: value }))

  const setFee = (key: string, value: number) =>
    setForm((f) => ({ ...f, fees: { ...f.fees, [key]: value } }))

  const setFeature = (key: string, value: boolean) =>
    setForm((f) => ({ ...f, features: { ...f.features, [key]: value } }))

  const togglePolicyTag = (category: string, tagValue: string, checked: boolean) =>
    setForm((f) => {
      const current = f.policy_tags[category] || []
      const updated = checked
        ? [...current, tagValue]
        : current.filter((v: string) => v !== tagValue)
      const newTags = { ...f.policy_tags }
      if (updated.length > 0) {
        newTags[category] = updated
      } else {
        delete newTags[category]
      }
      return { ...f, policy_tags: newTags }
    })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.institution_id) { setError('Please select an institution'); return }
    if (!form.product_name.trim()) { setError('Product name is required'); return }
    if (form.interest_rate <= 0) { setError('Interest rate must be greater than 0'); return }
    if (form.comparison_rate <= 0) { setError('Comparison rate must be greater than 0'); return }
    setLoading(true)
    setError('')
    try {
      await onSubmit(form)
      onClose()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              {isEdit ? 'Edit Loan Product' : 'Add Loan Product'}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Institution + Product Name */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Institution <span className="text-red-500">*</span>
              </label>
              <select
                value={form.institution_id}
                onChange={(e) => set('institution_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Select institution...</option>
                {institutions.filter(i => i.is_active).map((i) => (
                  <option key={i.id} value={i.id}>{i.name} ({i.code})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Product Type <span className="text-red-500">*</span>
              </label>
              <select
                value={form.product_type}
                onChange={(e) => set('product_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {PRODUCT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Product Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.product_name}
              onChange={(e) => set('product_name', e.target.value)}
              placeholder="e.g. Premier Advantage Home Loan"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              maxLength={255}
              required
            />
          </div>

          {/* Rates + LVR */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Interest Rate (%) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={form.interest_rate || ''}
                onChange={(e) => set('interest_rate', parseFloat(e.target.value) || 0)}
                placeholder="6.14"
                step="0.01"
                min="0.01"
                max="30"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Comparison Rate (%) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={form.comparison_rate || ''}
                onChange={(e) => set('comparison_rate', parseFloat(e.target.value) || 0)}
                placeholder="6.21"
                step="0.01"
                min="0.01"
                max="30"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max LVR (%) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={form.max_lvr || ''}
                onChange={(e) => set('max_lvr', parseFloat(e.target.value) || 0)}
                placeholder="95"
                step="1"
                min="1"
                max="100"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          {/* Fees */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Fees (optional)</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Application Fee ($)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={formatNumberWithCommas(String(form.fees.application_fee || ''))}
                  onChange={(e) => setFee('application_fee', parseFloat(stripToNumber(e.target.value)) || 0)}
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Annual Fee ($/yr)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={formatNumberWithCommas(String(form.fees.annual_fee || ''))}
                  onChange={(e) => setFee('annual_fee', parseFloat(stripToNumber(e.target.value)) || 0)}
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Discharge Fee ($)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={formatNumberWithCommas(String(form.fees.discharge_fee || ''))}
                  onChange={(e) => setFee('discharge_fee', parseFloat(stripToNumber(e.target.value)) || 0)}
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
            </div>
          </div>

          {/* Features */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Features</h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: 'offset_account', label: 'Offset Account' },
                { key: 'redraw_facility', label: 'Redraw Facility' },
                { key: 'extra_repayments', label: 'Extra Repayments' },
                { key: 'portability', label: 'Portability' },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!form.features[key as keyof typeof form.features]}
                    onChange={(e) => setFeature(key, e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Policy Eligibility Tags */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-1">Policy Eligibility Tags</h3>
            <p className="text-xs text-gray-500 mb-2">Leave empty = shown as "May be eligible" for all customers</p>
            <div className="space-y-1">
              {Object.entries(POLICY_TAG_CATEGORIES).map(([catKey, cat]) => {
                const selected = (form.policy_tags[catKey] || []) as string[]
                return (
                  <details key={catKey} className="border border-gray-200 rounded-lg">
                    <summary className="px-3 py-2 cursor-pointer text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center justify-between">
                      <span>{cat.label}</span>
                      {selected.length > 0 && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                          {selected.length} selected
                        </span>
                      )}
                    </summary>
                    <div className="px-3 pb-3 pt-1 grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {cat.tags.map((tag) => (
                        <label key={tag.value} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selected.includes(tag.value)}
                            onChange={(e) => togglePolicyTag(catKey, tag.value, e.target.checked)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="text-xs text-gray-700">{tag.label}</span>
                        </label>
                      ))}
                    </div>
                  </details>
                )
              })}
            </div>
          </div>

          {/* Active + Source Note */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => set('is_active', e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Active (visible in comparison tool)</span>
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Source Note (optional)</label>
              <input
                type="text"
                value={form.source_note ?? ''}
                onChange={(e) => set('source_note', e.target.value)}
                placeholder="e.g. ANZ website 02-Mar-2026"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors font-medium"
            >
              {loading ? 'Saving...' : isEdit ? 'Update Product' : 'Add Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
